/* eslint-disable indent */
import {
  CompanyFields,
  AllCompaniesFields,
  FormCompanyProps,
} from 'modules/companies/interfaces';
import i18n from 'i18n';
import { formatDuration } from './helpers';

/* eslint-disable no-restricted-syntax */
export interface BatchRequest {
  url: string;
  method: 'PUT' | 'POST' | 'GET' | 'PATCH' | 'DELETE';
  data: any;
}

export const trimValues = (
  startValues: any,
  changedItems: string[],
  values: any
) => {
  for (const key in startValues) {
    if (typeof startValues[key] === 'object') {
      const startValue = startValues[key];
      const value = values[key];

      const subKeys = Object.keys(startValue);

      for (const subKey of subKeys) {
        if (startValue[subKey] !== value[subKey]) {
          changedItems.push(key);
          break;
        }
      }
    }
  }
};

export const removePayloadKeys = (obj: any, payloadKeysToRemove: string[]) => {
  const updatedPayload = { ...obj };
  payloadKeysToRemove.forEach(key => delete updatedPayload[key]);
  return updatedPayload;
};

export const formatDateForDateFilter = (dateString: string): string => {
  const date = new Date(dateString);

  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date
    .getUTCDate()
    .toString()
    .padStart(2, '0');

  const hours = date
    .getUTCHours()
    .toString()
    .padStart(2, '0');
  const minutes = date
    .getUTCMinutes()
    .toString()
    .padStart(2, '0');
  const seconds = date
    .getUTCSeconds()
    .toString()
    .padStart(2, '0');

  const result = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}-0300`;

  return result;
};

export const getCompanyName = (
  company: CompanyFields | AllCompaniesFields | FormCompanyProps | any
) => {
  if (company?.display_name) {
    return company?.display_name;
  }
  if (company?.alias) {
    return company?.alias;
  }
  if (company?.legal_name) {
    return company?.legal_name;
  }
  if (company?.trading_name) {
    return company?.trading_name;
  }
  return company?.company_name;
};

export const adjustLuminosity = (hex: string, lum: number) => {
  let rgb = '#';
  for (let i = 0; i < 3; i += 1) {
    let c = parseInt(hex.substr(i * 2 + 1, 2), 16);
    c = Math.round(Math.min(Math.max(0, c + (c * lum) / 100), 255));
    const cStr = c.toString(16);
    rgb += `00${cStr}`.substr(cStr.length);
  }
  return rgb;
};

export const generateRandomColor = (baseColors: string[]) => {
  const randomBaseColor =
    baseColors[Math.floor(Math.random() * baseColors.length)];
  const luminosity = Math.floor(Math.random() * 40) - 20;

  return adjustLuminosity(randomBaseColor, luminosity);
};

export const calcTotalTime = (durations): string => {
  const totalTime = durations?.reduce(
    (acc, item) => acc + item?.duration_milliseconds,
    0
  );
  return formatDuration(totalTime);
};

export const formatDateWithMonth = (isoDate: string): string => {
  const date = new Date(isoDate);
  const language = i18n.language || 'pt-BR';

  const monthsPt = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];

  const monthsEs = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];

  const monthsEn = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  let months;
  switch (language) {
    case 'pt-BR':
      months = monthsPt;
      break;
    case 'es-ES':
    case 'es':
      months = monthsEs;
      break;
    case 'en-US':
    case 'en':
      months = monthsEn;
      break;
    default:
      months = monthsPt;
      break;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${month} ${day}, ${hours}:${minutes}`;
};

export const isValidVerificationCode = (code: string[]): boolean => {
  return code?.every(num => num !== '' && /^[0-9]$/.test(num));
};

export const convertSecondsToMinutes = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}min ${remainingSeconds}s`;
};

export const getSummaryCSV = (
  filtersString: any,
  searchValue: string,
  action: (filtersDashboard: any) => void
) => {
  let query = '';

  if (filtersString) {
    query += filtersString;
  }

  if (searchValue) {
    query += query ? `&search=${searchValue}` : `search=${searchValue}`;
  }

  action({ filtersDashboard: query });
};

export const formatNumberBenchmark = (
  value: number | null,
  locale = 'pt-BR'
): string => {
  if (value === null) return '-';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};
